import React, { useRef, useState } from 'react';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import * as Yup from 'yup';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

import { PasswordValidationRules } from 'utils/authUtils';
import PasswordValidationRuleBox from 'features/auth/PasswordValidationRuleBox';
import FormItem from 'components/formik/FormItem';
import SubmitButton from 'components/formik/SubmitButton';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  changePassword,
  sendCodeActivateTwoFA,
} from 'redux/reducers/accountSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppSelector } from 'hooks/useAppSelector';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import EnableConfirmTwoFAModal from 'components/modals/EnableConfirmTwoFAModal';
import { TwoFactorAuthSendMessageTypeEnum } from 'api/backofficeApiClient';

import styles from '../../AccountSettingsPage.module.less';

const requiredMessage = 'Field is required';

const validationSchema = Yup.object({
  oldPassword: Yup.string().required(requiredMessage),
  newPassword: Yup.string()
    .required(requiredMessage)
    .matches(PasswordValidationRules.MinimumCharacters)
    .matches(PasswordValidationRules.MaximumCharacters)
    .matches(PasswordValidationRules.LowercaseCharacter)
    .matches(PasswordValidationRules.SpecialCharacter)
    .matches(PasswordValidationRules.UppercaseCharacter)
    .matches(PasswordValidationRules.OneNumber),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords do not match')
    .required(requiredMessage),
});

export interface UpdatePasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const initialValues: UpdatePasswordFormValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};
export default function ChangePasswordSection() {
  const [isEnable2FAModalOpen, setIsEnable2FAModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const formRef: any = useRef();

  const handleViewEnable2FAModal = () => {
    setIsEnable2FAModalOpen(true);
  };

  const handleCloseEnable2FAModal = () => {
    setIsEnable2FAModalOpen(false);
  };

  const handleSendTwoFACode = async () => {
    const result = await dispatch(
      sendCodeActivateTwoFA(TwoFactorAuthSendMessageTypeEnum.ResetPassword),
    );

    if (sendCodeActivateTwoFA.fulfilled.match(result)) {
      handleViewEnable2FAModal();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const resetForm = () => {
    formRef.current.resetForm();
  };

  const handleSubmit = async (values: UpdatePasswordFormValues) => {
    const { confirmPassword, ...passwords } = values;

    const result = await dispatch(changePassword(passwords));

    if (changePassword.fulfilled.match(result)) {
      if (user?.isBOTwoFactorAuthStatus) {
        await handleSendTwoFACode();
      } else {
        BaseAlert.success('Password was successfully reset.');
        resetForm();
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <>
      {isEnable2FAModalOpen && (
        <EnableConfirmTwoFAModal
          passwordFormValues={formRef?.current.values}
          type={TwoFactorAuthSendMessageTypeEnum.ResetPassword}
          onSuccess={resetForm}
          onClose={handleCloseEnable2FAModal}
        />
      )}
      <h4 className={styles.title}>Change password</h4>
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form className={styles.authPageForm} layout="vertical">
            <div className={styles.authPageFormInput}>
              <FormItem name="oldPassword" label="Current password *">
                <Input.Password
                  placeholder="Enter current password"
                  size="middle"
                  name="oldPassword"
                  iconRender={(visible: boolean) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  autoComplete="off"
                />
              </FormItem>
              <FormItem
                name="newPassword"
                label="New password *"
                showValidateError={
                  formik.errors.newPassword === requiredMessage
                }
              >
                <Input.Password
                  placeholder="Enter new password"
                  size="middle"
                  name="newPassword"
                  iconRender={(visible: boolean) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  autoComplete="off"
                />
              </FormItem>
              <FormItem name="confirmPassword" label="Re-enter new password *">
                <Input.Password
                  placeholder="Re-enter new password"
                  size="middle"
                  name="confirmPassword"
                  iconRender={(visible: boolean) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  autoComplete="off"
                />
              </FormItem>
            </div>
            <div className={styles.authPageFormBtn}>
              <SubmitButton type="primary" size="large">
                Change
              </SubmitButton>
            </div>
            <PasswordValidationRuleBox password={formik.values.newPassword} />
          </Form>
        )}
      </Formik>
    </>
  );
}
